import React, { useCallback, useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import isEmpty from 'lodash/isEmpty';
import { Text, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { ORIGINAL_ESTIMATE, STORY_POINTS } from '../../../../model/constants.tsx';
import type { CardId } from '../../../../model/types.tsx';
import { useBacklogSelector } from '../../../../state/index.tsx';
import { getIsCompanyManaged } from '../../../../state/selectors/app/index.tsx';
import { getEstimationType } from '../../../../state/selectors/estimation/index.tsx';
import { getFilters } from '../../../../state/selectors/filters/index.tsx';
import { getIsRemainingTimeTrackingEnabledSelector } from '../../../../state/selectors/tracking/index.tsx';
import { messages } from './messages.tsx';
import {
	getTotalIssues,
	getRemainingEstimate,
	useFormatEstimationValue,
	useFilteredIssueCount,
	useFilteredRemainingEstimate,
	useFormatTimeTrackingValue,
} from './utils.tsx';

export function DividerStatistics(
	props:
		| {
				dividerCardListId: string;
				isDragIndicator?: false;
		  }
		| {
				dividerCardListId: string;
				isDragIndicator: true;
				droppingOverCardId: CardId;
				droppingOverCardListId: string;
				edge: 'top' | 'bottom';
		  },
) {
	const { dividerCardListId, isDragIndicator } = props;
	const { formatMessage } = useIntl();

	const estimationType = useBacklogSelector(getEstimationType);
	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);

	const totalIssues = useBacklogSelector((state) =>
		getTotalIssues(state, {
			cardListId: dividerCardListId,
			dropIndicator: isDragIndicator === true ? props : undefined,
		}),
	);
	const remainingEstimates = useBacklogSelector((state) =>
		getRemainingEstimate(state, {
			cardListId: dividerCardListId,
			dropIndicator: isDragIndicator === true ? props : undefined,
		}),
	);

	const filteredIssues = useFilteredIssueCount(dividerCardListId);

	const filters = useBacklogSelector(getFilters);
	const isFilterOn = !isEmpty(filters);
	const isDisplayRemainTimeEnabled = useBacklogSelector(getIsRemainingTimeTrackingEnabledSelector);
	const shouldShowFilteredCount = isFilterOn && !isDragIndicator;

	const filteredRemainingEstimates = useFilteredRemainingEstimate(dividerCardListId);

	const isRemainDifferentFromOriginal = shouldShowFilteredCount
		? filteredRemainingEstimates.originalTimeEstimate !==
			filteredRemainingEstimates.remainingTimeEstimate
		: remainingEstimates.originalTimeEstimate !== remainingEstimates.remainingTimeEstimate;

	const formatEstimationValue = useFormatEstimationValue();
	const formatTimeTrackingValue = useFormatTimeTrackingValue();

	const formatStrong = useCallback(
		(chunks: ReactNode[]) => (
			<Text
				size="medium"
				color={isVisualRefreshEnabled() && isDragIndicator ? 'color.text.inverse' : undefined}
				as="strong"
				weight={isVisualRefreshEnabled() && isDragIndicator ? 'bold' : undefined}
			>
				{chunks}
			</Text>
		),
		[isDragIndicator],
	);

	const getElementsText = useMemo(() => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return shouldShowFilteredCount
				? formatMessage(messages.filteredAmountCardsIssueTermRefresh, {
						cardAmount: totalIssues,
						filteredIssues,
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})
				: formatMessage(
						isDragIndicator && isVisualRefreshEnabled()
							? messages.totalCardsDragIndicatorIssueTermRefresh
							: messages.totalCardsIssueTermRefresh,
						{
							cardAmount: totalIssues,
							strong: (chunks: ReactNode[]) => formatStrong(chunks),
						},
					);
		}

		return shouldShowFilteredCount
			? formatMessage(messages.filteredAmountCards, {
					cardAmount: totalIssues,
					filteredIssues,
					strong: (chunks: ReactNode[]) => formatStrong(chunks),
				})
			: formatMessage(
					isDragIndicator && isVisualRefreshEnabled()
						? messages.totalCardsDragIndicator
						: messages.totalCards,
					{
						cardAmount: totalIssues,
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					},
				);
	}, [
		filteredIssues,
		formatMessage,
		formatStrong,
		isDragIndicator,
		shouldShowFilteredCount,
		totalIssues,
	]);

	const elements = [<div key="total">{getElementsText}</div>];

	const infoVerticalDivider = (prefix: string) => (
		<InfoVerticalDivider key={`${prefix}VerticalDivider`} isDragIndicator={isDragIndicator} />
	);

	const remainingEstimateState = (
		<div key="remainingEstimate">
			{shouldShowFilteredCount
				? formatMessage(messages.filteredRemainingLabel, {
						estimate: formatTimeTrackingValue(remainingEstimates.remainingTimeEstimate),
						filteredEstimate: formatTimeTrackingValue(
							filteredRemainingEstimates.remainingTimeEstimate,
						),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})
				: formatMessage(messages.remainingLabel, {
						estimate: formatTimeTrackingValue(remainingEstimates.remainingTimeEstimate),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})}
		</div>
	);

	const storyPointState = (
		<div key="storyPoints">
			{shouldShowFilteredCount
				? formatMessage(messages.filteredEstimateLabel, {
						estimate: formatEstimationValue(remainingEstimates.storyPoints),
						filteredEstimate: formatEstimationValue(filteredRemainingEstimates.storyPoints),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})
				: formatMessage(messages.estimateLabel, {
						estimate: formatEstimationValue(remainingEstimates.storyPoints),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})}
		</div>
	);

	const originalEstimateState = (
		<div key="originalEstimate">
			{shouldShowFilteredCount
				? formatMessage(messages.filteredEstimateLabel, {
						estimate: formatEstimationValue(remainingEstimates.originalTimeEstimate),
						filteredEstimate: formatEstimationValue(
							filteredRemainingEstimates.originalTimeEstimate,
						),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})
				: formatMessage(messages.estimateLabel, {
						estimate: formatEstimationValue(remainingEstimates.originalTimeEstimate),
						strong: (chunks: ReactNode[]) => formatStrong(chunks),
					})}
		</div>
	);

	if (estimationType === STORY_POINTS) {
		if (isCompanyManaged && isDisplayRemainTimeEnabled) {
			elements.push(...[infoVerticalDivider('remaining'), remainingEstimateState]);
		}

		elements.push(...[infoVerticalDivider('storyPoint'), storyPointState]);
	} else if (estimationType === ORIGINAL_ESTIMATE) {
		if (isCompanyManaged) {
			// in a CMP board
			// show remaining estimate only if it's different from original estimate
			// and Time Tracking is set to "Remaining Estimate and Time Spent"
			isDisplayRemainTimeEnabled &&
				isRemainDifferentFromOriginal &&
				elements.push(...[infoVerticalDivider('remaining'), remainingEstimateState]);

			// always show original estimate
			elements.push(...[infoVerticalDivider('originalEstimate'), originalEstimateState]);
		} else {
			// TMP
			if (remainingEstimates.remainingTimeEstimate > 0) {
				elements.push(...[infoVerticalDivider('remaining'), remainingEstimateState]);
			}
			if (remainingEstimates.originalTimeEstimate > 0) {
				elements.push(...[infoVerticalDivider('originalEstimate'), originalEstimateState]);
			}
		}
	}

	return isVisualRefreshEnabled() ? (
		<Flex
			alignItems="center"
			justifyContent="start"
			xcss={[
				dividerStatisticsContainerStyles,
				isDragIndicator ? dragIndicatorTextStyles : defaultTextStyles,
			]}
		>
			{elements}
		</Flex>
	) : (
		<DividerStatisticsContainer isDragIndicator={isDragIndicator}>
			{elements}
		</DividerStatisticsContainer>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DividerStatisticsContainer = styled.div<{
	isDragIndicator?: boolean;
}>({
	font: token('font.body.UNSAFE_small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isDragIndicator }) =>
		isDragIndicator
			? token('color.text.inverse')
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.subtlest'),
	display: 'flex',
	alignItems: 'center',
	columnGap: token('space.150'),
	justifyContent: 'start',
	flexWrap: 'wrap',
	marginLeft: token('space.100'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InfoVerticalDivider = styled.div<{ isDragIndicator?: boolean }>({
	width: '1px',
	height: token('space.200'),
	borderRightStyle: 'solid',
	borderRightWidth: '1px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	borderRightColor: ({ isDragIndicator }) =>
		isDragIndicator
			? token('color.text.inverse')
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				token('color.text.subtlest'),
	opacity: 0.4,
});

const dividerStatisticsContainerStyles = xcss({
	font: 'font.body',
	columnGap: 'space.100',
	flexWrap: 'wrap',
});

const dragIndicatorTextStyles = xcss({
	color: 'color.text.inverse',
});

const defaultTextStyles = xcss({
	color: 'color.text.subtlest',
});
